import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export type ActionStatus = 'pending' | 'completed' | 'cancelled';

export interface ActionDetail {
  actionId: string;
  actionType: string;
  requiredGroupRef: string;
  targetUserRef: string;
  title: string;
  description: string;
  link: string;
  severity: 'low' | 'normal' | 'high' | 'critical';
  status: ActionStatus;
  createdAt: string;
  completedAt: string | null;
  completedByRef: string | null;

  /** ID of the Backstage notification sent for this action, if one was created. */
  notificationId: string | null;

  /** Whether the current viewer is authorized to access this action. */
  viewerAllowed: boolean;

  /** Entity reference of the viewer resolved by the backend. */
  viewerRef: string;
}

export interface CreateActionInput {
  actionId: string;
  actionType: string;
  requiredGroupRef: string;
  targetUserRef: string;
  title: string;
  description: string;
  link: string;
  severity: 'low' | 'normal' | 'high' | 'critical';
}

export interface ActionNotificationsApi {
  getAction(
    actionId: string,
    testViewerRef?: string,
  ): Promise<ActionDetail>;

  completeAction(actionId: string): Promise<ActionDetail>;

  /** Creates an action through the trusted service endpoint. */
  createAction(input: CreateActionInput): Promise<ActionDetail>;

  /** Creates an action through the development-only test endpoint. */
  createTestAction(input: CreateActionInput): Promise<ActionDetail>;
}

export const actionNotificationsApiRef =
  createApiRef<ActionNotificationsApi>({
    id: 'plugin.action-notifications.service',
  });

export class ActionNotificationsClient
  implements ActionNotificationsApi
{
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('action-notifications');
  }

  private async getErrorMessage(
    response: Response,
  ): Promise<string> {
    const body: unknown = await response.json().catch(() => undefined);

    if (
      body &&
      typeof body === 'object' &&
      'error' in body
    ) {
      const error = (body as {
        error?: { message?: unknown } | string;
      }).error;

      if (typeof error === 'string' && error.trim()) {
        return error;
      }

      if (
        error &&
        typeof error === 'object' &&
        typeof error.message === 'string' &&
        error.message.trim()
      ) {
        return error.message;
      }
    }

    return `${response.status} ${response.statusText}`.trim();
  }

  async getAction(
    actionId: string,
    testViewerRef?: string,
  ): Promise<ActionDetail> {
    const base = await this.baseUrl();
    const params = new URLSearchParams();

    if (testViewerRef) {
      params.set('testViewerRef', testViewerRef);
    }

    const query = params.toString();
    const url = `${base}/actions/${encodeURIComponent(actionId)}${
      query ? `?${query}` : ''
    }`;

    const response = await this.fetchApi.fetch(url);

    if (!response.ok) {
      const message = await this.getErrorMessage(response);

      throw new Error(
        `Failed to load action ${actionId}: ${message}`,
      );
    }

    return (await response.json()) as ActionDetail;
  }

  async completeAction(
    actionId: string,
  ): Promise<ActionDetail> {
    const base = await this.baseUrl();

    const response = await this.fetchApi.fetch(
      `${base}/actions/${encodeURIComponent(actionId)}/complete`,
      {
        method: 'POST',
      },
    );

    if (!response.ok) {
      const message = await this.getErrorMessage(response);

      throw new Error(
        `Failed to complete action ${actionId}: ${message}`,
      );
    }

    return (await response.json()) as ActionDetail;
  }

  async createAction(
    input: CreateActionInput,
  ): Promise<ActionDetail> {
    return this.postAction('/actions', input);
  }

  async createTestAction(
    input: CreateActionInput,
  ): Promise<ActionDetail> {
    return this.postAction('/test/actions', input);
  }

  private async postAction(
    path: string,
    input: CreateActionInput,
  ): Promise<ActionDetail> {
    const base = await this.baseUrl();

    const response = await this.fetchApi.fetch(`${base}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      const message = await this.getErrorMessage(response);

      throw new Error(`Action request failed: ${message}`);
    }

    return (await response.json()) as ActionDetail;
  }
}
